import React from 'react';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';

const statusConfig = {
  not_started: { label: 'Not Started', color: 'bg-stone-100 text-stone-600' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  complete: { label: 'Complete', color: 'bg-emerald-100 text-emerald-700' }
};

const EssayListView = ({ onSelectEssay, onAddNew }) => {
  const { essayDrafts, programs, deleteEssayDraft } = useCollegePrepContext();

  const getProgramName = (programId) => {
    if (!programId) return null;
    return programs.find(p => p.id === programId)?.name || null;
  };

  const getWordCount = (draft) => {
    if (draft.builderData?.wordCount) return draft.builderData.wordCount;
    if (draft.content?.trim()) return draft.content.trim().split(/\s+/).length;
    return 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Group drafts: grouped by applicationGroupId, ungrouped are standalone
  const { groups, standalone } = React.useMemo(() => {
    const groupMap = {};
    const standaloneList = [];

    essayDrafts.forEach(draft => {
      if (draft.applicationGroupId) {
        if (!groupMap[draft.applicationGroupId]) {
          groupMap[draft.applicationGroupId] = [];
        }
        groupMap[draft.applicationGroupId].push(draft);
      } else {
        standaloneList.push(draft);
      }
    });

    // Sort groups by creation date (newest first)
    const groupList = Object.entries(groupMap)
      .map(([groupId, drafts]) => ({
        groupId,
        drafts: drafts.sort((a, b) => a.id.localeCompare(b.id)),
        programName: getProgramName(drafts[0]?.programId),
        programUrl: drafts[0]?.programUrl,
        contextNotes: drafts[0]?.contextNotes,
        createdAt: drafts[0]?.createdAt
      }))
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

    return { groups: groupList, standalone: standaloneList };
  }, [essayDrafts, programs]);

  if (essayDrafts.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-4xl mb-3">✍️</div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Essays Yet</h3>
        <p className="text-sm text-stone-600 mb-4 max-w-md mx-auto">
          Add essay prompts from your summer program applications and use the guided builder to craft strong responses.
        </p>
        <button
          onClick={onAddNew}
          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
        >
          Add Your First Essay
        </button>
      </div>
    );
  }

  const renderDraftCard = (draft, showNumber = null) => {
    const status = statusConfig[draft.status] || statusConfig.not_started;
    const wordCount = getWordCount(draft);

    return (
      <div
        key={draft.id}
        className="flex items-start gap-3 p-3 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors"
        onClick={() => onSelectEssay(draft)}
      >
        {showNumber !== null && (
          <span className="text-xs font-semibold text-violet-600 bg-violet-50 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            {showNumber}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 line-clamp-2 mb-1">
            {draft.promptText || 'Untitled'}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
            <span className={`px-2 py-0.5 rounded-full font-medium ${status.color}`}>
              {status.label}
            </span>
            <span className="bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
              {draft.mode === 'guided-essay' ? '📝 Essay' : '💬 Response'}
            </span>
            <span>
              {wordCount}{draft.wordLimit ? ` / ${draft.wordLimit}` : ''} words
            </span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Delete this draft?')) {
              deleteEssayDraft(draft.id);
            }
          }}
          className="text-stone-400 hover:text-red-500 text-xs p-1 shrink-0"
          title="Delete"
        >
          &times;
        </button>
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">
          Your Essays
          <span className="ml-2 text-sm font-normal text-stone-500">({essayDrafts.length})</span>
        </h2>
        <button
          onClick={onAddNew}
          className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
        >
          + Add Essay
        </button>
      </div>

      <div className="space-y-3">
        {/* Grouped applications */}
        {groups.map((group) => (
          <div key={group.groupId} className="border border-stone-200 rounded-xl overflow-hidden bg-white">
            {/* Group header */}
            <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {group.programName && (
                      <span className="text-sm font-semibold text-slate-900">{group.programName}</span>
                    )}
                    <span className="text-xs text-stone-500">{group.drafts.length} questions</span>
                  </div>
                  {group.programUrl && (
                    <a
                      href={group.programUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-violet-600 hover:text-violet-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Program website
                    </a>
                  )}
                </div>
                {group.contextNotes && (
                  <span className="text-xs text-stone-400" title={group.contextNotes}>
                    Has notes
                  </span>
                )}
              </div>
            </div>
            {/* Group questions */}
            <div className="divide-y divide-stone-100">
              {group.drafts.map((draft, i) => renderDraftCard(draft, i + 1))}
            </div>
          </div>
        ))}

        {/* Standalone drafts */}
        {standalone.map((draft) => {
          const programName = getProgramName(draft.programId);
          return (
            <div key={draft.id} className="border border-stone-200 rounded-xl bg-white">
              {programName && (
                <div className="px-4 py-2 bg-stone-50 border-b border-stone-100">
                  <span className="text-xs font-medium text-sky-700">{programName}</span>
                </div>
              )}
              {renderDraftCard(draft)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EssayListView;
